import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  DollarSign,
  Target,
  BarChart3,
  Users,
  CheckCircle,
  TrendingUp,
  Shield,
  Heart
} from 'lucide-react';

const CSRInvestors = () => {
  const sponsorModels = [
    {
      name: "Per Child Sponsorship",
      amount: "$28 USD (15,820 CFA / 2,030 GMD)",
      period: "per child/year",
      description: "Sponsor individual children's healthcare coverage",
      benefits: [
        "Direct impact on child health",
        "Personalized reporting",
        "Photo updates and stories",
        "Tax-deductible contribution"
      ]
    },
    {
      name: "School Sponsorship",
      amount: "500,000 CFA",
      period: "per school/year",
      description: "Sponsor an entire school's health program",
      benefits: [
        "School-wide health impact",
        "Custom health programs",
        "Brand visibility opportunities",
        "Comprehensive reporting"
      ]
    },
    {
      name: "District Sponsorship",
      amount: "5,000,000 CFA",
      period: "per district/year",
      description: "Sponsor health programs across a district",
      benefits: [
        "Regional health impact",
        "Policy influence opportunities",
        "Major brand visibility",
        "Strategic enrollment benefits"
      ]
    }
  ];

  const visibilityOptions = [
    {
      title: "Co-branded Health Days",
      description: "Joint health awareness events with your branding"
    },
    {
      title: "Impact Dashboards",
      description: "Real-time data on your sponsored children's health outcomes"
    },
    {
      title: "Annual Reports",
      description: "Comprehensive impact reports with case studies and metrics"
    },
    {
      title: "Media Coverage",
      description: "Press releases and media coverage of your impact"
    }
  ];

  const impactKPIs = [
    {
      metric: "Children Reached",
      target: "10,000+",
      description: "Number of children with healthcare coverage"
    },
    {
      metric: "Health Improvements",
      target: "95%",
      description: "Percentage of children receiving annual checkups"
    },
    {
      metric: "Family Savings",
      target: "80%",
      description: "Reduction in out-of-pocket healthcare costs"
    },
    {
      metric: "School Attendance",
      target: "25%",
      description: "Increase in school attendance rates"
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative section-padding bg-gradient-to-br from-primary-600 to-accent-600 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="/urgentcare_full_site_plan.png" 
            alt="AMHAS Healthcare" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">CSR & Investors</h1>
            <p className="text-xl md:text-2xl text-primary-100">
              Invest in AI + Western telehealth for schoolchildren: school vitals → AI triage → doctor review, with clinic referrals only for procedures.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Sponsor Models */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Sponsor Models</h2>
            <p className="section-subtitle">
              Flexible sponsorship options to fit your budget and impact goals
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {sponsorModels.map((model, index) => (
              <motion.div
                key={model.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`card p-8 ${index === 1 ? 'ring-2 ring-primary-600 relative' : ''}`}
              >
                {index === 1 && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{model.name}</h3>
                  <div className="text-3xl font-bold text-primary-600 mb-1">{model.amount}</div>
                  <div className="text-gray-600 mb-4">{model.period}</div>
                  <p className="text-gray-600">{model.description}</p>
                </div>

                <ul className="space-y-3 mb-8">
                  {model.benefits.map((benefit, benefitIndex) => (
                    <li key={benefitIndex} className="flex items-start space-x-3">
                      <CheckCircle size={16} className="text-accent-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{benefit}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/schedule-call"
                  className={`w-full text-center py-3 px-6 rounded-lg font-semibold transition-all duration-300 ${index === 1
                    ? 'bg-primary-600 hover:bg-primary-700 text-white'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                    }`}
                >
                  Learn More
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Visibility Options */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Visibility & Recognition</h2>
            <p className="section-subtitle">
              Showcase your commitment to child health and community impact
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {visibilityOptions.map((option, index) => (
              <motion.div
                key={option.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card p-8"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-3">{option.title}</h3>
                <p className="text-gray-600">{option.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact KPIs */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Impact KPIs & Audit Options</h2>
            <p className="section-subtitle">
              Transparent reporting and verification of your social impact
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {impactKPIs.map((kpi, index) => (
              <motion.div
                key={kpi.metric}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card p-6 text-center"
              >
                <div className="text-3xl font-bold text-primary-600 mb-2">{kpi.target}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{kpi.metric}</h3>
                <p className="text-gray-600 text-sm">{kpi.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <div className="card p-8 max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Audit & Verification</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <BarChart3 size={32} className="text-primary-600 mx-auto mb-2" />
                  <div className="text-lg font-semibold text-gray-900">Third-Party Audits</div>
                  <div className="text-gray-600 text-sm">Independent verification of impact metrics</div>
                </div>
                <div className="text-center">
                  <Shield size={32} className="text-accent-600 mx-auto mb-2" />
                  <div className="text-lg font-semibold text-gray-900">Transparent Reporting</div>
                  <div className="text-gray-600 text-sm">Regular reports with detailed metrics</div>
                </div>
                <div className="text-center">
                  <CheckCircle size={32} className="text-green-600 mx-auto mb-2" />
                  <div className="text-lg font-semibold text-gray-900">Compliance Standards</div>
                  <div className="text-gray-600 text-sm">Adherence to international reporting standards</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Investment Thesis */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Investment Thesis</h2>
            <p className="section-subtitle">
              Why AMHAS represents a compelling social investment opportunity
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Market Opportunity</h3>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <TrendingUp size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">1M+ children in Cameroon & The Gambia need affordable healthcare</span>
                </li>
                <li className="flex items-start space-x-3">
                  <TrendingUp size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">50M+ children across West Africa represent expansion potential</span>
                </li>
                <li className="flex items-start space-x-3">
                  <TrendingUp size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Growing demand for preventive healthcare solutions</span>
                </li>
                <li className="flex items-start space-x-3">
                  <TrendingUp size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Government support for school-based health programs</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Financial Model</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-primary-50 rounded-lg">
                  <span className="text-gray-700">Premium per child</span>
                  <span className="font-bold text-primary-600">$28 USD (15,820 CFA / 2,030 GMD)</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-accent-50 rounded-lg">
                  <span className="text-gray-700">Expected claims</span>
                  <span className="font-bold text-accent-600">5,000-6,000 CFA</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-secondary-50 rounded-lg">
                  <span className="text-gray-700">Admin & reserves</span>
                  <span className="font-bold text-secondary-600">1,500-2,000 CFA</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="text-gray-700">Surplus per child</span>
                  <span className="font-bold text-green-600">500-1,000 CFA</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary-600 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-4">Ready to Invest in Impact?</h2>
            <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
              Join us in creating a healthier future for children across Cameroon & The Gambia.
              Download our investor materials and schedule a call to learn more.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <a href="/schedule-call" className="btn-secondary">
                Schedule Investor Call
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CSRInvestors;
